import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ListOrdered } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { useListCompetitionsQuery } from '@/services/qclansApi';
import { classifyQclansError, qclansErrorKey } from '@/services/qclansErrors';
import { formatDateShort } from '@/lib/format';
import type { Competition, CompetitionStatus } from '@/types/qclans';
import { cn } from '@/lib/cn';

/**
 * QClans competitions list (`GET /v1/competitions`). Competitions are
 * QLASH-owned and clan/metric-based (never individual 1v1 — the CoC API can't
 * create matches). Tapping one opens its leaderboard standings.
 */
export function CocCompetitions() {
  const { t } = useTranslation('coc');
  const query = useListCompetitionsQuery();
  const competitions = query.data?.competitions ?? [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <ListOrdered aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('competitions.title')}</h1>
        </div>
        <p className="text-sm text-fg-muted">{t('competitions.subtitle')}</p>
      </header>

      {query.isLoading ? (
        <Loader />
      ) : query.error ? (
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {t(qclansErrorKey(classifyQclansError(query.error)))}
        </p>
      ) : competitions.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-muted">{t('competitions.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {competitions.map((c) => (
            <li key={c.competitionId}>
              <CompetitionCard competition={c} />
            </li>
          ))}
        </ul>
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}

function CompetitionCard({ competition }: { competition: Competition }) {
  const { t } = useTranslation('coc');
  const haptics = useHaptics();
  const c = competition;

  const statusKnown = (s: CompetitionStatus): s is 'upcoming' | 'active' | 'ended' =>
    s === 'upcoming' || s === 'active' || s === 'ended';

  return (
    <Link
      to={`/coc/competitions/${encodeURIComponent(c.competitionId)}`}
      onClick={() => haptics.impact('light')}
      className="flex flex-col gap-2 rounded-2xl border border-brand/40 bg-brand/5 p-4 transition-opacity active:opacity-80"
    >
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-base font-bold text-fg">{c.name}</h2>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
            c.status === 'active'
              ? 'bg-success/20 text-success'
              : c.status === 'ended'
                ? 'bg-bg-tertiary text-fg-muted'
                : 'bg-warning/20 text-warning',
          )}
        >
          {statusKnown(c.status) ? t(`competitions.status.${c.status}`) : c.status}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="rounded bg-bg-tertiary px-2 py-0.5 font-semibold text-fg-muted">
          {t(`competitions.metric.${c.metric}`)}
        </span>
        <span className="rounded bg-bg-tertiary px-2 py-0.5 font-semibold text-fg-muted">
          {t(`competitions.subject.${c.subjectType}`)}
        </span>
      </div>
      <p className="text-[11px] text-fg-muted">
        {t('competitions.dateRange', {
          start: formatDateShort(c.startsAt),
          end: formatDateShort(c.endsAt),
        })}
      </p>
    </Link>
  );
}
